<template>
  <div class="rtx-wrap">

    <!-- ✅ 객실/플랜 화면 -->
    <RtxRoomPlan
      v-if="roomPlanMode"
      :hotel-info="selectedHotel"
      :search-data="selectedSearchData"
      :margin-map="marginMap"
      @select-plan="onPlanSelected"
      @go-back="roomPlanMode = false"
    />

    <!-- ✅ 예약 화면 -->
    <RtxBooking
      v-else-if="bookingMode"
      :hotel-info="selectedHotel"
      :search-data="selectedSearchData"
      @booking-complete="onBookingComplete"
      @booking-failed="() => bookingMode = false"
    />

    <!-- ✅ 검색 화면 -->
    <template v-else>

      <!-- 상단 검색바 -->
      <div class="search-bar-wrap">
        <div class="search-bar">

          <!-- 키워드 검색 -->
          <div class="sb-field sb-keyword" style="position:relative;">
            <span class="sb-icon">🔎</span>
            <div class="sb-inner">
              <label class="sb-label">호텔 검색</label>
              <input v-model="searchKeyword" @input="onKeywordInput" @focus="onKeywordInput"
                type="text" class="sb-input" placeholder="호텔명(국문/영문)" autocomplete="off"/>
            </div>
            <button v-if="searchKeyword" @click="clearKeyword" class="keyword-clear">✕</button>

            <!-- 자동완성 드롭다운 -->
            <div v-if="keywordOpen && keywordSuggestions.length" class="sb-suggest-panel" @click.stop>
              <div
                v-for="h in keywordSuggestions"
                :key="h.hotel_id"
                class="suggest-item"
                @click="selectKeyword(h)"
              >
                <span class="suggest-name">{{ h.hotel_name }}</span>
                <span class="suggest-loc">{{ h.state_province || h.city || '' }}</span>
              </div>
            </div>
          </div>

          <div class="sb-divider"></div>

          <!-- 지역 -->
          <div class="sb-field sb-region" :class="{ 'sb-error': validErr.region }">
            <span class="sb-icon">📍</span>
            <div class="sb-inner">
              <label class="sb-label">지역 <em>*</em></label>
              <select v-model="filter.city" @change="onCityChange" class="sb-select">
                <option value="">지역을 선택하세요</option>
                <option v-for="c in cityList" :key="c.state_province"
                  :value="c.state_province">{{ c.pref_name_ko }}</option>
              </select>
            </div>
          </div>

          <div class="sb-divider"></div>

          <!-- 숙소 유형 -->
          <div class="sb-field sb-type">
            <span class="sb-icon">🏨</span>
            <div class="sb-inner">
              <label class="sb-label">숙소 유형</label>
              <select v-model="filter.typeCode" @change="onFilterChange" class="sb-select">
                <option value="">전체 유형</option>
                <option v-for="t in typeList" :key="t.code" :value="t.code">{{ t.label_ko }}</option>
              </select>
            </div>
          </div>

          <div class="sb-divider"></div>

          <!-- 체크인 - 체크아웃 (통합 캘린더) -->
          <div class="sb-field sb-dates" :class="{ 'sb-error': validErr.checkIn || validErr.checkOut }"
            @click="toggleCalendar" style="cursor:pointer; position:relative;">
            <span class="sb-icon">📅</span>
            <div class="sb-inner">
              <label class="sb-label">체크인 - 체크아웃 <em>*</em></label>
              <div class="sb-date-summary">
                <span v-if="form.checkIn">{{ formatDisplay(form.checkIn) }}</span>
                <span v-else class="sb-placeholder">날짜 선택</span>
                <span class="sb-arrow">→</span>
                <span v-if="form.checkOut">{{ formatDisplay(form.checkOut) }}</span>
                <span v-else class="sb-placeholder">-</span>
              </div>
            </div>

            <!-- 캘린더 패널 -->
            <div v-if="calendarOpen" class="sb-cal-panel" @click.stop>
              <div class="cal-header">
                <button type="button" class="cal-nav" @click="navCalendar(-1)">‹</button>
                <div class="cal-months-label">
                  <span>{{ calendarMonths[0].label }}</span>
                  <span>{{ calendarMonths[1].label }}</span>
                </div>
                <button type="button" class="cal-nav" @click="navCalendar(1)">›</button>
              </div>

              <div class="cal-months">
                <div class="cal-month" v-for="(m, mi) in calendarMonths" :key="mi">
                  <div class="cal-weekdays">
                    <span v-for="wd in weekdayLabels" :key="wd">{{ wd }}</span>
                  </div>
                  <div class="cal-days">
                    <span
                      v-for="(cell, ci) in m.days"
                      :key="ci"
                      class="cal-day"
                      :class="{
                        'cal-empty': !cell.date,
                        'cal-disabled': cell.disabled,
                        'cal-start': cell.isStart,
                        'cal-end': cell.isEnd,
                        'cal-in-range': cell.inRange,
                        'cal-today': cell.isToday
                      }"
                      @click="cell.date && selectDate(cell.date, cell.disabled)"
                    >{{ cell.day }}</span>
                  </div>
                </div>
              </div>

              <div class="cal-footer">
                <span class="cal-hint">{{ calendarHint }}</span>
                <button type="button" class="cal-clear" @click.stop="clearDates">초기화</button>
              </div>
            </div>
          </div>

          <!-- 박수 표시 -->
          <div v-if="nightCount > 0" class="sb-nights">{{ nightCount }}박</div>

          <div class="sb-divider"></div>

          <!-- 인원 -->
          <div class="sb-field sb-guests" :class="{ 'sb-error': validErr.guests }"
            @click="guestPanelOpen = !guestPanelOpen"
            style="cursor:pointer; position:relative;">
            <span class="sb-icon">👥</span>
            <div class="sb-inner">
              <label class="sb-label">인원 / 객실 <em>*</em></label>
              <div class="sb-guest-summary">
                성인 {{ form.adultCount }}명 · {{ form.roomCount }}실
                <span v-if="form.children.length"> · 어린이 {{ form.children.length }}명</span>
              </div>
            </div>

            <!-- 인원 드롭다운 패널 -->
            <div v-if="guestPanelOpen" class="guest-panel" @click.stop>
              <div class="guest-row">
                <span>객실 수</span>
                <div class="guest-ctrl">
                  <button @click="form.roomCount = Math.max(1, form.roomCount - 1)" class="gc-btn">－</button>
                  <span class="gc-val">{{ form.roomCount }}</span>
                  <button @click="form.roomCount = Math.min(9, form.roomCount + 1)" class="gc-btn">＋</button>
                </div>
              </div>
              <div class="guest-row">
                <span>성인</span>
                <div class="guest-ctrl">
                  <button @click="form.adultCount = Math.max(form.roomCount, form.adultCount - 1)" class="gc-btn">－</button>
                  <span class="gc-val">{{ form.adultCount }}</span>
                  <button @click="form.adultCount = Math.min(36, form.adultCount + 1)" class="gc-btn">＋</button>
                </div>
              </div>
              <div class="guest-row">
                <span>어린이</span>
                <div class="guest-ctrl">
                  <button @click="removeChild(form.children.length - 1)"
                    :disabled="form.children.length === 0" class="gc-btn">－</button>
                  <span class="gc-val">{{ form.children.length }}</span>
                  <button @click="addChild" :disabled="form.children.length >= 9" class="gc-btn">＋</button>
                </div>
              </div>
              <div v-if="form.children.length > 0" class="child-ages">
                <div v-for="(age, i) in form.children" :key="i" class="child-age-row">
                  <span>어린이 {{ i + 1 }} 나이</span>
                  <select v-model.number="form.children[i]" class="sb-select" style="width:80px;">
                    <option v-for="a in 18" :key="a - 1" :value="a - 1">{{ a - 1 }}세</option>
                  </select>
                </div>
              </div>
              <button @click="guestPanelOpen = false" class="guest-done-btn">완료</button>
            </div>
          </div>

          <!-- 검색 버튼 -->
          <button @click="doSearch"
            :disabled="loading || targetHotelIds.length === 0"
            class="sb-search-btn">
            <span v-if="loading" class="sb-spinner"></span>
            <span v-else>🔍</span>
            {{ loading ? '검색 중' : '검색' }}
          </button>

        </div>

        <div v-if="errMsg" class="search-errmsg">⚠️ {{ errMsg }}</div>

        <div v-if="!errMsg && filter.city" class="search-info">
          <span class="info-dot"></span>
          {{ filter.city ? `${filter.city} ·` : '' }}
          검색 대상 <strong>{{ targetHotelIds.length }}</strong>개 호텔
          <span v-if="searchKeyword" class="keyword-badge">
            🔎 "{{ searchKeyword }}" 검색 중
          </span>
        </div>
      </div>

      <!-- 검색바 아래, 모바일에서만 표시 -->
      <div class="mobile-filter-bar">
        <button @click="filterDrawerOpen = true" class="mobile-filter-btn">
          <i class="bi bi-sliders"></i> 맞춤검색
          <span v-if="activeFilterCount > 0" class="mobile-filter-badge">
            {{ activeFilterCount }}
          </span>
        </button>
        <span v-if="searched" class="mobile-result-count">
          <strong>{{ filteredCards.length }}</strong>개 호텔
        </span>
      </div>

      <!-- 모바일 드로어 오버레이 -->
      <div v-if="filterDrawerOpen"
        class="filter-drawer-overlay"
        @click="filterDrawerOpen = false">
      </div>

      <!-- 모바일 드로어 -->
      <div class="filter-drawer" :class="{ 'drawer-open': filterDrawerOpen }">
        <div class="drawer-header">
          <span>🔧 맞춤검색</span>
          <div class="drawer-header-btns">
            <button @click="resetSideFilter" class="fp-reset">초기화</button>
            <button @click="filterDrawerOpen = false" class="drawer-close-btn">✕</button>
          </div>
        </div>
        <div class="drawer-body">

          <!-- 1. 가격대 -->
          <div class="fp-section" v-if="searched && sortedCards.length">
            <div class="fp-section-title">가격대</div>
            <div class="fp-price-labels">
              <span>{{ krw(sideFilter.priceMin) }}원</span>
              <span>~</span>
              <span>{{ krw(sideFilter.priceMax) }}원</span>
            </div>
            <div class="fp-dual-slider">
              <div class="fp-track">
                <div class="fp-track-fill" :style="trackFillStyle"></div>
              </div>
              <input type="range" class="fp-range fp-range-min"
                :min="priceRangeBounds.min" :max="priceRangeBounds.max"
                :step="priceRangeBounds.step"
                v-model.number="sideFilter.priceMin"
                @input="onPriceMinChange" />
              <input type="range" class="fp-range fp-range-max"
                :min="priceRangeBounds.min" :max="priceRangeBounds.max"
                :step="priceRangeBounds.step"
                v-model.number="sideFilter.priceMax"
                @input="onPriceMaxChange" />
            </div>
          </div>

          <!-- 2. 성급 -->
          <div class="fp-section">
            <div class="fp-section-title">성급</div>
            <div class="fp-check-list">
              <label v-for="s in [5,4,3,2,1]" :key="s" class="fp-check-item">
                <input type="checkbox" :value="s" v-model="sideFilter.stars" />
                <span class="fp-stars">{{ '★'.repeat(s) }}{{ '☆'.repeat(5-s) }}</span>
                <span class="fp-count">({{ starCount(s) }})</span>
              </label>
              <label class="fp-check-item">
                <input type="checkbox" :value="0" v-model="sideFilter.stars" />
                <span>등급없음</span>
                <span class="fp-count">({{ starCount(0) }})</span>
              </label>
            </div>
          </div>

          <!-- 3. 평점 -->
          <div class="fp-section">
            <div class="fp-section-title">TrustYou 평점</div>
            <label class="fp-toggle-item">
              <input type="checkbox" v-model="sideFilter.minRating" />
              <span>7.0점 이상만 보기</span>
            </label>
          </div>

          <!-- 4. 무료취소 -->
          <div class="fp-section" v-if="searched">
            <div class="fp-section-title">취소 정책</div>
            <label class="fp-toggle-item">
              <input type="checkbox" v-model="sideFilter.freeCancel" />
              <span>무료취소 가능만 보기</span>
            </label>
          </div>

          <!-- 5. 흡연/금연 (검색 후에만) -->
          <div class="fp-section" v-if="searched">
            <div class="fp-section-title">금연/흡연룸</div>
            <div class="fp-check-list">
              <label class="fp-check-item">
                <input type="radio" value="" v-model="sideFilter.smoking" />
                <span>전체</span>
              </label>
              <label class="fp-check-item">
                <input type="radio" value="smoking" v-model="sideFilter.smoking" />
                <span>흡연룸 포함만</span>
                <span class="fp-count">({{ smokingCount('smoking') }})</span>
              </label>
            </div>
          </div>

          <!-- 6. 편의시설 -->
          <div class="fp-section" v-if="activeFacilityList.length">
            <div class="fp-section-title">편의시설</div>
            <div class="fp-check-list">
              <label v-for="f in visibleFacilityList" :key="f.facility_code" class="fp-check-item">
                <input type="checkbox"
                  :value="f.facility_code"
                  v-model="sideFilter.amenities" />
                <span>{{ f.description_ko || f.description_en }}</span>
                <span class="fp-count">({{ f.cnt }})</span>
              </label>
            </div>
            <button
              v-if="activeFacilityList.length > 10"
              @click="showAllFacilities = !showAllFacilities"
              class="fp-more-btn">
              {{ showAllFacilities ? '▲ 접기' : `▼ 더보기 (+${activeFacilityList.length - 10}개)` }}
            </button>
          </div>

          <!-- 7. 객실 종류 -->
          <div class="fp-section" v-if="searched && roomTypeOptions.length">
            <div class="fp-section-title">객실 종류</div>
            <div class="fp-check-list">
              <label v-for="rt in roomTypeOptions" :key="rt.label" class="fp-check-item">
                <input type="checkbox" :value="rt.label" v-model="sideFilter.roomTypes" />
                <span>{{ rt.label }}</span>
                <span class="fp-count">({{ rt.cnt }})</span>
              </label>
            </div>
          </div>

        </div>
        <div class="drawer-footer">
          <button @click="filterDrawerOpen = false" class="drawer-apply-btn">
            결과 보기 ({{ filteredCards.length }}개)
          </button>
        </div>
      </div>


      <!-- 로딩 -->
      <!-- 검색 클릭 이후엔(searched=true) 전면 스피너 대신 카드별 pending 표시 사용 -->
      <div v-if="loading && !searched" class="loading-wrap">
      <!-- <div v-if="loading" class="loading-wrap"> -->
        <div class="loading-card">
          <div class="loading-anim">
            <div class="ld-bar" v-for="n in 5" :key="n"
              :style="`animation-delay:${(n-1)*0.12}s`"></div>
          </div>
          <p>호텔 요금을 조회하고 있습니다...</p>
        </div>
      </div>

      <!-- 지역 미선택 안내 -->
      <div v-if="!filter.city && !searchKeyword" class="empty-guide">
        <!-- <div v-if="!loading && !filter.city && !searchKeyword" class="empty-guide">   -->
        <div class="guide-icon">🗾</div>
        <h3>여행할 지역을 선택해주세요</h3>
        <p>지역을 선택하거나 호텔명을 검색해주세요.</p>
      </div>

      <!-- 결과 영역 -->
      <!-- <div v-if="!loading && (filter.city || searchKeyword)" class="results-wrap"> -->
      <div v-if="filter.city || searchKeyword" class="results-wrap">

        <div class="results-header">
          <div class="results-title">
            <span v-if="searched">
              <strong>{{ filteredCards.length }}</strong>개 호텔 검색됨
              <span class="results-date">
                {{ form.checkIn }} ~ {{ form.checkOut }} ({{ nightCount }}박)
              </span>
              <span v-if="filteredCards.length !== sortedCards.length" class="filter-active-badge">
                필터 적용 중 (전체 {{ sortedCards.length }}개)
              </span>
            </span>
            <span v-else>
              <strong>{{ filteredHotelList.length }}</strong>개 호텔 검색대기
              <span class="results-date">· 날짜와 인원 입력 후 검색 버튼을 눌러주세요</span>
            </span>
          </div>
          <div v-if="searched" class="sort-wrap">
            <select v-model="sortKey" class="sort-select">
              <option value="price_asc">가격 낮은순</option>
              <option value="price_desc">가격 높은순</option>
              <option value="trust_rating">TrustYou 평점순</option>
              <option value="rating">성급순</option>
            </select>
          </div>
        </div>

        <!-- ✅ 2단 레이아웃 -->
        <div class="results-body">

          <!-- 좌측 필터 패널 -->
          <aside class="filter-panel">
            <div class="fp-title">
              <span><i class="bi bi-sliders2"></i> 맞춤검색</span>
              <button @click="resetSideFilter" class="fp-reset">초기화</button>
            </div>

            <!-- 1. 가격대 (검색 후에만) -->
            <div class="fp-section" v-if="searched && sortedCards.length">
              <div class="fp-section-title">가격대</div>
              <div class="fp-price-labels">
                <span>{{ krw(sideFilter.priceMin) }}원</span>
                <span>~</span>
                <span>{{ krw(sideFilter.priceMax) }}원</span>
              </div>
              <div class="fp-dual-slider">
                <div class="fp-track">
                  <div class="fp-track-fill" :style="trackFillStyle"></div>
                </div>
                <input type="range" class="fp-range fp-range-min"
                  :min="priceRangeBounds.min" :max="priceRangeBounds.max"
                  :step="priceRangeBounds.step"
                  v-model.number="sideFilter.priceMin"
                  @input="onPriceMinChange" />
                <input type="range" class="fp-range fp-range-max"
                  :min="priceRangeBounds.min" :max="priceRangeBounds.max"
                  :step="priceRangeBounds.step"
                  v-model.number="sideFilter.priceMax"
                  @input="onPriceMaxChange" />
              </div>
            </div>

            <!-- 2. 성급 -->
            <div class="fp-section">
              <div class="fp-section-title">성급</div>
              <div class="fp-check-list">
                <label v-for="s in [5,4,3,2,1]" :key="s" class="fp-check-item">
                  <input type="checkbox" :value="s" v-model="sideFilter.stars" />
                  <span class="fp-stars">{{ '★'.repeat(s) }}{{ '☆'.repeat(5-s) }}</span>
                  <span class="fp-count">({{ starCount(s) }})</span>
                </label>
                <label class="fp-check-item">
                  <input type="checkbox" :value="0" v-model="sideFilter.stars" />
                  <span>등급없음</span>
                  <span class="fp-count">({{ starCount(0) }})</span>
                </label>
              </div>
            </div>

            <!-- 3. 평점 -->
            <div class="fp-section">
              <div class="fp-section-title">TrustYou 평점</div>
              <label class="fp-toggle-item">
                <input type="checkbox" v-model="sideFilter.minRating" />
                <span>7.0점 이상만 보기</span>
              </label>
            </div>

            <!-- 4. 무료취소 (검색 후에만) -->
            <div class="fp-section" v-if="searched">
              <div class="fp-section-title">취소 정책</div>
              <label class="fp-toggle-item">
                <input type="checkbox" v-model="sideFilter.freeCancel" />
                <span>무료취소 가능만 보기</span>
              </label>
            </div>

            <!-- 5. 흡연/금연 (검색 후에만) -->
            <div class="fp-section" v-if="searched">
              <div class="fp-section-title">흡연룸유무</div>
              <div class="fp-check-list">
                <label class="fp-check-item">
                  <input type="radio" value="" v-model="sideFilter.smoking" />
                  <span>전체</span>
                </label>
                <label class="fp-check-item">
                  <input type="radio" value="smoking" v-model="sideFilter.smoking" />
                  <span>흡연룸 포함만</span>
                  <span class="fp-count">({{ smokingCount('smoking') }})</span>
                </label>
              </div>
            </div>

            <!-- 6. 편의시설 -->
            <div class="fp-section" v-if="activeFacilityList.length">
              <div class="fp-section-title">편의시설</div>
              <div class="fp-check-list">
                <label v-for="f in visibleFacilityList" :key="f.facility_code" class="fp-check-item">
                  <input type="checkbox"
                    :value="f.facility_code"
                    v-model="sideFilter.amenities" />
                  <span>{{ f.description_ko || f.description_en }}</span>
                  <span class="fp-count">({{ f.cnt }})</span>
                </label>
              </div>

              <button
                v-if="activeFacilityList.length > 10"
                @click="showAllFacilities = !showAllFacilities"
                class="fp-more-btn">
                {{ showAllFacilities ? '▲ 접기' : `▼ 더보기 (+${activeFacilityList.length - 10}개)` }}
              </button>
            </div>

            <!-- 7. 객실 종류 (검색 후에만) -->
            <div class="fp-section" v-if="searched && roomTypeOptions.length">
              <div class="fp-section-title">객실 종류</div>
              <div class="fp-check-list">
                <label v-for="rt in roomTypeOptions" :key="rt.label" class="fp-check-item">
                  <input type="checkbox" :value="rt.label" v-model="sideFilter.roomTypes" />
                  <span>{{ rt.label }}</span>
                  <span class="fp-count">({{ rt.cnt }})</span>
                </label>
              </div>
            </div>
          </aside>

          <!-- 우측 카드 리스트 -->
          <div class="card-area">

            <div class="card-list">
              <div v-for="hotel in filteredCards" :key="hotel.hotel_id"
                class="hotel-card" :class="{ 'card-has-price': priceMap[hotel.hotel_id] }">

                <!-- 이미지 캐러셀 -->
                <div class="card-img-wrap" :data-hotel-id="hotel.hotel_id"
                  :ref="el => registerCard(el, hotel.hotel_id)">

                  <div v-if="imageLoadingMap[hotel.hotel_id]" class="card-img-placeholder img-loading">
                    <div class="img-spinner"></div>
                  </div>

                  <template v-else-if="imageMap[hotel.hotel_id]?.length">
                    <img :src="currentImage(hotel.hotel_id)" class="card-img"
                      @error="e => e.target.src = ''" :alt="hotel.hotel_name" />
                    <template v-if="imageMap[hotel.hotel_id].length > 1">
                      <button class="carousel-btn carousel-prev" @click.stop="prevImage(hotel.hotel_id)">‹</button>
                      <button class="carousel-btn carousel-next" @click.stop="nextImage(hotel.hotel_id)">›</button>
                      <div class="carousel-dots">
                        <span v-for="(_, i) in imageMap[hotel.hotel_id]" :key="i"
                          class="carousel-dot"
                          :class="{ 'dot-active': carouselIndexMap[hotel.hotel_id] === i }"
                          @click.stop="setImage(hotel.hotel_id, i)"></span>
                      </div>
                      <div class="carousel-counter">
                        {{ (carouselIndexMap[hotel.hotel_id] ?? 0) + 1 }} / {{ imageMap[hotel.hotel_id].length }}
                      </div>
                    </template>
                  </template>

                  <div v-else class="card-img-placeholder">🏨</div>

                  <span v-if="hotel.category_name_ko" class="card-type-badge">
                    {{ hotel.category_name_ko }}
                  </span>
                </div>

                <!-- 호텔 정보 -->
                <div class="card-info">
                  <div class="card-hotel-name">
                    {{ hotel.hotel_name }} ({{ hotel.original_name }})
                    <span class="hotel-grade-badge" :class="hotel.rating ? 'grade-has' : 'grade-none'">
                      {{ hotel.rating ? `${hotel.rating}성급` : '등급없음' }}
                    </span>
                  </div>
                  <div class="card-location">
                    <span class="loc-icon">📍</span>
                    <span>{{ hotel.state_province || hotel.city || '-' }}</span>
                  </div>
                  <div>
                    <span style="font-size:0.85rem; color:#666;">{{ hotel.address }}</span>
                  </div>

                  <!-- 객실 정보 태그 -->
                  <div v-if="priceMap[hotel.hotel_id]" class="card-room-tags">
                    <span v-if="priceMap[hotel.hotel_id].room_desc" class="room-tag rt-room">
                      🛏 {{ priceMap[hotel.hotel_id].room_desc }}
                    </span>
                    <span v-if="priceMap[hotel.hotel_id].supplier_desc" class="room-tag rt-bed">
                      {{ priceMap[hotel.hotel_id].supplier_desc }}
                    </span>
                    <span v-if="priceMap[hotel.hotel_id].food_code" class="room-tag rt-food">
                      🍽 {{ FOOD[priceMap[hotel.hotel_id].food_code] || '' }}
                    </span>
                    <span v-if="priceMap[hotel.hotel_id].cancel_policy" class="room-tag rt-cancel">
                      📋 {{ cancelSummary(priceMap[hotel.hotel_id].cancel_policy) }}
                    </span>
                  </div>

                  <div style="flex:1"></div>

                  <!-- TrustYou 평점 -->
                  <div class="card-rating-row">
                    <div v-if="hotel.trustyou_rating != null" class="rating-badge">
                      <span class="rating-score">{{ (hotel.trustyou_rating / 10).toFixed(1) }}</span>
                      <div class="rating-meta">
                        <span class="rating-source">TrustYou</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- 가격 영역 -->
                <div class="card-price-wrap">
                  <!-- <div v-if="!priceMap[hotel.hotel_id]" class="price-pending">
                    <div class="price-pending-icon">🔍</div>
                    <span>날짜·인원 입력 후<br>검색해주세요</span>
                  </div> -->
                  <div v-if="!priceMap[hotel.hotel_id]" class="price-pending">
                    <template v-if="searched && allBatchesDone">
                      <div class="price-pending-icon">😥</div>
                      <span>현재 예약 가능한<br>객실이 없습니다</span>
                    </template>
                    <template v-else-if="searched">
                      <div class="price-pending-spinner"></div>
                      <span>요금 조회 중...</span>
                    </template>
                    <template v-else>
                      <div class="price-pending-icon">🔍</div>
                      <span>날짜·인원 입력 후<br>검색해주세요</span>
                    </template>
                  </div>

                  <template v-else>
                    <div class="price-block">
                      <div class="price-refund-top">
                        <span :class="priceMap[hotel.hotel_id].non_refundable ? 'badge-nr' : 'badge-r'">
                          {{ priceMap[hotel.hotel_id].non_refundable ? '환불불가' : '환불가능' }}
                        </span>
                      </div>
                      <div class="price-selling-main">
                        {{ krw(priceMap[hotel.hotel_id].selling_krw) }}<small>원</small>
                      </div>
                      <div v-if="nightCount > 0" class="price-per-night">
                        1박 기준 약 {{ krw(Math.round(priceMap[hotel.hotel_id].selling_krw / nightCount)) }}원
                        · {{ nightCount }}박
                      </div>
                      <div v-if="priceMap[hotel.hotel_id].extra_usd > 0" class="price-extra-fees">
                        <span class="extra-label">세금·리조트피</span>
                        <span class="extra-val">
                          USD {{ priceMap[hotel.hotel_id].extra_usd.toFixed(2) }} 별도
                        </span>
                      </div>
                    </div>
                    <button @click="doSelect(hotel)" class="btn-book">객실&플랜보기 →</button>
                  </template>
                </div>

              </div>
            </div>

            <!-- 키워드 검색 결과 없음 -->
            <div v-if="!loading && searchKeyword && filteredHotelList.length === 0"
              class="empty-result">
              <div class="empty-icon">🔍</div>
              <h3>"{{ searchKeyword }}" 검색 결과가 없습니다</h3>
              <p>한글검색이 안될 경우, 영문으로 검색을 해보세요.</p>
              <p>다른 키워드로 검색하거나 조건을 변경해 보세요.</p>
              <button @click="clearKeyword" class="btn-clear-kw">검색어 초기화</button>
            </div>

            <!-- API 검색 결과 없음 -->
            <div v-if="searched && batchesDone >= batchesTotal && hotels.length === 0 && !searchKeyword" class="empty-result">
              <div class="empty-icon">🔎</div>
              <h3>검색 결과가 없습니다</h3>
              <p>한글검색이 안될 경우, 영문으로 검색을 해보세요.</p>
              <p>날짜, 인원 또는 지역 조건을 변경해 보세요.</p>
            </div>

            <!-- 필터 결과 없음 -->
            <div v-if="searched && filteredCards.length === 0 && sortedCards.length > 0"
              class="empty-result">
              <div class="empty-icon">😥</div>

              <h3>조건에 맞는 호텔이 없습니다</h3>
              <p>한글검색이 안될 경우, 영문으로 검색을 해보세요.</p>
              <p>필터 조건을 변경해 보세요.</p>
              <button @click="resetSideFilter" class="btn-clear-kw">필터 초기화</button>
            </div>

          </div><!-- /card-area -->

        </div><!-- /results-body -->

      </div><!-- /results-wrap -->

    </template>

  </div>
</template>


<script>
import RtxBooking  from '@/components/RtxBookingB2C.vue'
import RtxRoomPlan from '@/components/RtxRoomPlanB2C.vue'

export default {
  name: 'RtxHotelSearch',

  components: { RtxBooking, RtxRoomPlan },

  props: {
    fixedHotelIds: { type: Array, default: () => [] }
  },

  emits: ['select-hotel'],

  FOOD: {
    1:'룸온리', 2:'조식포함', 3:'중식포함',
    4:'석식포함', 5:'하프보드(2식포함)', 6:'풀보드(3식포함)', 7:'올인클루시브(식사,음료,부대시설이용)'
  },

  data() {
    return {
      bookingMode:        false,
      roomPlanMode:       false,
      selectedHotel:      null,
      selectedSearchData: null,

      filter: { city: '', typeCode: '' },
      searchKeyword: '',
      keywordOpen: false,
      keywordSuggestions: [], // ← computed에서 data로 변경 (서버 검색 결과 저장용)
      searchTimer: null,      // ← 디바운스 타이머
      searchToken: 0,         // ← 응답 순서 꼬임 방지용

      form: {
        checkIn:    '',
        checkOut:   '',
        roomCount:  1,
        adultCount: 2,
        children:   []
      },

      allHotels:         [],
      filteredHotelList: [],
      cityList:          [],
      typeList:          [],
      nameMap:           {},
      marginMap:         {},

      hotels:    [],
      sessionId: null,

      loading: !!(this.$route.query.city && this.$route.query.checkIn),     //쿼리스트링에 검색조건이 있으면 true, 없으면 false
      searched:       false,
      errMsg:         '',
      validErr:       { region: false, checkIn: false, checkOut: false, guests: false },
      guestPanelOpen: false,
      calendarOpen:   false,
      calendarBase:   (() => { const t = new Date(); return new Date(t.getFullYear(), t.getMonth(), 1); })(),
      weekdayLabels:  ['일', '월', '화', '수', '목', '금', '토'],
      sortKey:        'price_asc',

      imageMap:         {},
      imageLoadingMap:  {},
      carouselIndexMap: {},

      // 좌측맞춤검색
      facilityList: [],

      sideFilter: {
        priceMin:   0,
        priceMax:   99999999,
        stars:      [],
        minRating:  false,
        freeCancel: false,
        amenities:  [],
        roomTypes:  [],
        smoking: ""               // 흡연 여부 (예: "smoking", "non-smoking", "")
      },

      showAllFacilities: false,   // ✅ 편의시설 더보기 상태
      showAllRoomTypes:  false,   // ✅ 객실종류 더보기 상태 (필요시)

      filterDrawerOpen: false,    // ✅ 모바일 필터 드로어 열림 상태

      // ✅ 배치/세션 폴링 관리용 추가
      batchSessions: {},          // { batchIndex: { session_id, session_id_krw } }
      pollingActive: false,
      pollTimers:    [],
      batchesTotal:   0,          // ✅ 추가 — 전체 배치 수
      batchesDone:    0,          // ✅ 추가 — 완료(성공/실패/포기) 처리된 배치 수      
    };
  },

  computed: {
    user()             { return this.$store.state.user; },
    FOOD()             { return this.$options.FOOD; },
    rakutenImageHost() { return this.$store.state.rakutenImageHost || ''; },

    today() { return new Date().toISOString().split('T')[0]; },

    maxDate() {
      const d = new Date(); d.setFullYear(d.getFullYear() + 1);
      return d.toISOString().split('T')[0];
    },

    calendarMonths() {
      const base = this.calendarBase;
      const m0 = this.buildMonth(base.getFullYear(), base.getMonth());
      const next = new Date(base.getFullYear(), base.getMonth() + 1, 1);
      const m1 = this.buildMonth(next.getFullYear(), next.getMonth());
      return [m0, m1];
    },

    calendarHint() {
      if (!this.form.checkIn) return '체크인 날짜를 선택해주세요';
      if (!this.form.checkOut) return '체크아웃 날짜를 선택해주세요';
      return `${this.nightCount}박 일정이 선택되었습니다`;
    },

    nightCount() {
      if (!this.form.checkIn || !this.form.checkOut) return 0;
      return Math.round(
        (new Date(this.form.checkOut) - new Date(this.form.checkIn)) / 86400000
      );
    },

    targetHotelIds() {
      if (this.fixedHotelIds.length > 0) return this.fixedHotelIds.slice(0, 100);
      return this.filteredHotelList.map(h => h.hotel_id).slice(0, 100);
    },

    // ← keywordSuggestions computed는 삭제 (data 프로퍼티로 대체됨)

    priceMap() {
      const map = {};
      this.hotels.forEach(h => {
        const taxUsd    = h.taxes_and_fees?.tax?.value    ?? 0;
        const resortUsd = h.hotel_fees?.resort_fee?.value ?? 0;

        map[h.hotel_id] = {
          cost_krw:            h.cost_krw,
          minimum_selling_krw: h.minimum_selling_krw ?? null,
          selling_krw:         this.calcSellingKrw(h.hotel_id, h.cost_krw, h.minimum_selling_krw),
          non_refundable:      h.room_details?.non_refundable,
          room_desc:           h.room_details?.description          || '',
          supplier_desc:       h.room_details?.supplier_description || '',
          food_code:           h.room_details?.food                 ?? null,
          room_type:           h.room_details?.room_type            || '',
          beds:                h.room_details?.beds                 || {},
          tax_usd:             taxUsd,
          resort_usd:          resortUsd,
          extra_usd:           taxUsd + resortUsd,
          cancel_policy:       h.cancellation_policy || null,
          booking_key:         h.booking_key         || '',
        };
      });
      return map;
    },

    sortedCards() {
      if (!this.searched) return [];

      // ✅ RTX 응답 여부와 무관하게, 검색 대상 호텔 전체를 베이스로 사용
      const rtxMap = new Map(this.hotels.map(h => [h.hotel_id, h]));

      const list = this.targetHotelIds
        .map(id => this.allHotels.find(ah => ah.hotel_id === id))
        .filter(Boolean)
        .map(dbInfo => {
          const rtx = rtxMap.get(dbInfo.hotel_id);
          return rtx ? { ...dbInfo, ...rtx } : { ...dbInfo };
        });

      list.sort((a, b) => {
        // ✅ 가격 도착한 카드 먼저, 미도착 카드는 뒤로
        const aHas = this.priceMap[a.hotel_id] != null;
        const bHas = this.priceMap[b.hotel_id] != null;
        if (aHas !== bHas) return aHas ? -1 : 1;

        const aS = this.calcSellingKrw(a.hotel_id, a.cost_krw, a.minimum_selling_krw) ?? 0;
        const bS = this.calcSellingKrw(b.hotel_id, b.cost_krw, b.minimum_selling_krw) ?? 0;

        if (this.sortKey === 'price_asc')  return aS - bS;
        if (this.sortKey === 'price_desc') return bS - aS;
        if (this.sortKey === 'trust_rating') return (b.trustyou_rating ?? 0) - (a.trustyou_rating ?? 0);
        if (this.sortKey === 'rating') {
          const aR = a.rating != null ? Number(a.rating) : -1;
          const bR = b.rating != null ? Number(b.rating) : -1;
          return bR - aR;
        }
        return 0;
      });

      return list;
    },

    // 좌측맞춤검색 (2026-05-19)
    trackFillStyle() {
      const { min, max } = this.priceRangeBounds;
      const range = max - min;
      if (range === 0) return {};
      const left  = ((this.sideFilter.priceMin - min) / range * 100).toFixed(1);
      const right = (100 - (this.sideFilter.priceMax - min) / range * 100).toFixed(1);
      return {
        left:  left  + '%',
        right: right + '%'
      };
    },

    // 가격 범위 경계값
    priceRangeBounds() {
      const prices = this.sortedCards
        .map(h => this.priceMap[h.hotel_id]?.selling_krw)
        .filter(p => p != null);
      if (!prices.length) return { min: 0, max: 2000000, step: 10000 };
      const min  = Math.floor(Math.min(...prices) / 10000) * 10000;
      const max  = Math.ceil(Math.max(...prices)  / 10000) * 10000;
      return { min, max, step: 10000 };
    },

    // 성급별 호텔 수
    starCount() {
      return (star) => {
        const list = this.searched ? this.sortedCards : this.filteredHotelList;
        if (star === 0) return list.filter(h => !h.rating).length;
        return list.filter(h => Number(h.rating) === star).length;
      };
    },

    // 현재 결과 기준 보유 시설 목록 (보유 호텔 수 포함)
    activeFacilityList() {
      const baseList = this.searched ? this.sortedCards : this.filteredHotelList;
      const countMap = {};

      baseList.forEach(h => {
        try 
        {
          const arr = typeof h.facilities === 'string'
            ? JSON.parse(h.facilities)
            : (h.facilities || []);
          arr.forEach(code => {
            countMap[code] = (countMap[code] || 0) + 1;
          });
        } 
        catch (e)
        {
          console.error(e);
        }
      });

      return this.facilityList
        .filter(f => (countMap[f.facility_code] || 0) > 0)
        .map(f => ({ ...f, cnt: countMap[f.facility_code] || 0 }))
        .sort((a, b) => b.cnt - a.cnt);
    },

    // ✅ 사이드 필터 적용된 최종 카드 목록
    filteredCards() {
      if (!this.searched) return [];

      return this.sortedCards.filter(h => {
        const pm = this.priceMap[h.hotel_id];

        // 1. 가격대
        if (pm) {
          const p = pm.selling_krw ?? 0;
          if (p < this.sideFilter.priceMin || p > this.sideFilter.priceMax) return false;
        }

        // 2. 성급
        if (this.sideFilter.stars.length) {
          const rating = h.rating ? Number(h.rating) : 0;
          if (!this.sideFilter.stars.includes(rating)) return false;
        }

        // 3. 평점 7점 이상
        if (this.sideFilter.minRating) {
          const score = h.trustyou_rating != null ? h.trustyou_rating / 10 : 0;
          if (score < 7.0) return false;
        }

        // 4. 무료취소
        if (this.sideFilter.freeCancel && pm?.non_refundable) return false;

        // 흡연/금연룸
        if (this.sideFilter.smoking) {
          if (this.sideFilter.smoking === 'non_smoking' && !(h.non_smoking_cnt > 0)) return false;
          if (this.sideFilter.smoking === 'smoking'     && !(h.smoking_cnt     > 0)) return false;
        }

        // 6. 편의시설
        if (this.sideFilter.amenities.length) {
          try {
            const arr = typeof h.facilities === 'string'
              ? JSON.parse(h.facilities)
              : (h.facilities || []);
            const hasAll = this.sideFilter.amenities.every(
              code => arr.includes(Number(code))
            );
            if (!hasAll) return false;
          } catch { return false; }
        }

        // 7. 객실 종류
        if (this.sideFilter.roomTypes.length) {
          if (!pm) 
          {
            // ✅ 배치가 다 끝났는데 가격이 없으면(=재고없음) 필터 결과에서도 제외
            if (this.allBatchesDone) return false;
            // 아직 조회 중이면 판단 보류하고 일단 통과(스피너로 표시됨)
          } 
          else 
          {
            const rt = (pm.room_type || '').toLowerCase();
            const matched = this.sideFilter.roomTypes.some(label => {
              const category = this.roomTypeOptions.find(o => o.label === label);
              return category?.keywords.some(kw => rt.includes(kw));
            });
            
            if (!matched) return false;
          }
        }
        return true;
      });
    },

    smokingCount() {
      return (type) => {
        return this.sortedCards.filter(h => {
          if (type === 'non_smoking') return (h.non_smoking_cnt ?? 0) > 0;
          if (type === 'smoking')     return (h.smoking_cnt     ?? 0) > 0;
          return false;
        }).length;
      };
    },

    visibleFacilityList() {
      return this.showAllFacilities ? this.activeFacilityList : this.activeFacilityList.slice(0, 10);
    },

    // 객실 종류 옵션 (검색 결과에서 추출<=라쿠텐에서 불완전하게 제공되는 room_type 보완)
    roomTypeOptions() {
      const categoryMap = {
        '스위트':   ['suite'],
        '디럭스':   ['deluxe'],
        '스탠다드': ['standard'],
        '트윈':     ['twin'],
        '더블':     ['double'],
        '싱글':     ['single'],
        '패밀리':   ['family'],
        '일본식':   ['japanese', 'tatami', '和室'],
      };

      const result = [];

      Object.entries(categoryMap).forEach(([label, keywords]) => {
        const cnt = this.sortedCards.filter(h => {
          const rt = (this.priceMap[h.hotel_id]?.room_type || '').toLowerCase();
          return keywords.some(kw => rt.includes(kw));
        }).length;

        if (cnt > 0) result.push({ label, keywords, cnt });
      });

      return result;
    },

    // 모바일화면용 맞춤검색필터
    activeFilterCount() {
      let cnt = 0;
      if (this.sideFilter.stars.length)     cnt += this.sideFilter.stars.length;
      if (this.sideFilter.minRating)        cnt++;
      if (this.sideFilter.freeCancel)       cnt++;
      if (this.sideFilter.smoking)          cnt++;   // 흡연/금연 필터도 카운트에 포함
      if (this.sideFilter.amenities.length) cnt += this.sideFilter.amenities.length;
      if (this.sideFilter.roomTypes.length) cnt += this.sideFilter.roomTypes.length;
      if (this.sideFilter.priceMin > this.priceRangeBounds.min) cnt++;
      if (this.sideFilter.priceMax < this.priceRangeBounds.max) cnt++;
      return cnt;
    },

    allBatchesDone() {
      return this.batchesTotal > 0 && this.batchesDone >= this.batchesTotal;
    },
  },

  created() {
    // ✅ HotelSearchBar에서 넘어온 쿼리스트링 적용
    const q = this.$route.query;
    if (q.city)       this.filter.city       = q.city;
    if (q.keyword)    this.searchKeyword     = q.keyword;
    if (q.typeCode)   this.filter.typeCode   = q.typeCode;    
    if (q.checkIn)    this.form.checkIn      = q.checkIn;
    if (q.checkOut)   this.form.checkOut     = q.checkOut;
    if (q.roomCount)  this.form.roomCount    = Number(q.roomCount);
    if (q.adultCount) this.form.adultCount   = Number(q.adultCount);
    if (q.children)   this.form.children     = q.children
      ? q.children.split(',').filter(Boolean).map(Number)
      : [];

    this.loadCityAndType();
    this.loadHotelList();
    this.$_observer = null;
  },

  mounted() {
    document.addEventListener('click', this.closePanels);

    this.$_observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const hotelId = entry.target.dataset.hotelId;
          if (hotelId) this.loadHotelImages(hotelId);
        }
      });
    }, { rootMargin: '100px', threshold: 0.1 });
  },

  beforeUnmount() {
    document.removeEventListener('click', this.closePanels);

    if (this.$_observer) this.$_observer.disconnect();

    clearTimeout(this.searchTimer);
    this.clearPollTimers();   // ✅ 추가 — 페이지 이탈 시 폴링 중단
  },

  methods: {

    // 좌측맞춤검색
    resetSideFilter() {
      this.sideFilter = {
        priceMin:   this.priceRangeBounds.min,
        priceMax:   this.priceRangeBounds.max,
        stars:      [],
        minRating:  false,
        freeCancel: false,
        smoking:    '',
        amenities:  [],
        roomTypes:  []
      };
      this.showAllFacilities = false;  // 더보기
      this.showAllRoomTypes  = false;  // 더보기
    },

    onPriceMinChange() {
      if (this.sideFilter.priceMin > this.sideFilter.priceMax - 10000)
        this.sideFilter.priceMin = this.sideFilter.priceMax - 10000;
    },

    onPriceMaxChange() {
      if (this.sideFilter.priceMax < this.sideFilter.priceMin + 10000)
        this.sideFilter.priceMax = this.sideFilter.priceMin + 10000;
    },


    async loadCityAndType() {
      try 
      {
        const [cities, types, facilities] = await Promise.all([
          this.$api('/api/perfectrureList',   { param: ['JP'] }),
          this.$api('/api/rakutenHotelType',  {}),
          this.$api('/api/getFacilitiesForSearch', {})   // 설비맞춤검색용
        ]);
        if (cities?.length)     this.cityList     = cities;
        if (types?.length)      this.typeList      = types;
        if (facilities?.length) this.facilityList  = facilities;  // ✅ 추가
      } 
      catch(e) 
      {
        console.error('[RTX] 기본데이터 로드 실패:', e);
      }
    },

    async loadHotelList() {
      try 
      {
        const hotels = await this.$api('/api/rakutenHotelsInfo', {});
        if (hotels?.length) 
        {
          this.allHotels = hotels
            .filter(h => h.salesFlag == null || h.salesFlag !== 'N')   // ← sFlag → salesFlag, NULL 허용
            .map(h => ({
              ...h,
              hotel_id: String(h.hotel_id || h.hotelId || h.id)
            }));
          this.filteredHotelList = [...this.allHotels];

          this.allHotels.forEach(h => {
            this.nameMap[h.hotel_id]   = h.hotel_name;   // ← name → hotel_name
            this.marginMap[h.hotel_id] = {
              margin:       h.margin       ?? 0,
              marginMethod: h.marginMethod ?? 'A'
            };
          });

          // ✅ 쿼리스트링으로 지역이 세팅된 경우 필터 적용 후 자동 검색
          if (this.filter.city) 
          {
            this.applyFilters();
            if (this.form.checkIn && this.form.checkOut) 
            {
              await this.doSearch();
              return;
            }
          }
        }
      } 
      catch(e) 
      {
        console.error('[RTX] 숙소리스트 로드실패:', e);
      }
      this.loading = false;  // ✅ 검색 없이 끝나는 경우 or 호텔 목록이 없는 경우
    },

    applyFilters() {
      const keyword  = this.searchKeyword.toLowerCase().trim();
      const typeCode = this.filter.typeCode ? String(this.filter.typeCode) : '';  // ✅ 문자열 통일

      this.filteredHotelList = this.allHotels.filter(h => {
        const cityOk  = !this.filter.city || h.state_province === this.filter.city;
        const typeOk  = !typeCode || String(h.category_id) === typeCode;           // ✅ 양쪽 String 변환
        const kwMatch = !keyword || (
          (h.hotel_name     && h.hotel_name.toLowerCase().includes(keyword))     ||  // ← name → hotel_name
          (h.city           && h.city.toLowerCase().includes(keyword))           ||
          (h.state_province && h.state_province.toLowerCase().includes(keyword)) ||
          (h.address        && h.address.toLowerCase().includes(keyword))        ||
          (h.hotel_id       && h.hotel_id.toLowerCase().includes(keyword))
        );
        return cityOk && typeOk && kwMatch;
      });
    },

    onCityChange()   { this.onFilterChange(); },
    onFilterChange() { this.applyFilters(); this.hotels = []; this.searched = false; },

    onKeywordInput() {
      this.applyFilters();       // ← 지역/유형 기반 targetHotelIds는 그대로 유지
      this.hotels = [];
      this.searched = false;

      const kw = this.searchKeyword.trim();
      this.keywordOpen = kw.length > 0;

      clearTimeout(this.searchTimer);
      if (!kw) {
        this.keywordSuggestions = [];
        return;
      }
      this.searchTimer = setTimeout(() => this.fetchSuggestions(kw), 300);
    },

    async fetchSuggestions(kw) {
      const myToken = ++this.searchToken;
      try
      {
        const like = `%${kw}%`;
        const result = await this.$api('/api/searchRakutenHotels', {
          param: [like, like, like, like]
        });

        if (myToken !== this.searchToken) return;  // 늦게 도착한 응답 무시

        this.keywordSuggestions = (result || []).map(h => ({
          ...h,
          hotel_id: String(h.hotel_id)
        }));
      }
      catch(e)
      {
        console.error('[RTX] 자동완성 검색 실패:', e);
      }
    },

    selectKeyword(hotel) {
      this.searchKeyword = hotel.hotel_name;   // ← name → hotel_name
      if (hotel.state_province) this.filter.city = hotel.state_province;
      this.keywordOpen = false;
      this.applyFilters();
      this.hotels = [];
      this.searched = false;
    },

    clearKeyword()   { this.searchKeyword = ''; this.keywordOpen = false; this.applyFilters(); },

    pad(n) {
      return String(n).padStart(2, '0');
    },

    toDateStr(year, month, day) {
      return `${year}-${this.pad(month + 1)}-${this.pad(day)}`;
    },

    addDays(dateStr, n) {
      const d = new Date(dateStr);
      d.setDate(d.getDate() + n);
      return d.toISOString().split('T')[0];
    },

    isDateDisabled(dateStr) {
      if (dateStr < this.today) return true;
      if (dateStr > this.maxDate) return true;
      // 체크아웃 대기 중엔 체크인 + 30일까지만 허용
      if (this.form.checkIn && !this.form.checkOut) {
        const cap = this.addDays(this.form.checkIn, 30);
        if (dateStr > cap) return true;
      }
      return false;
    },

    buildMonth(year, month) {
      const firstDay = new Date(year, month, 1);
      const startWeekday = firstDay.getDay();
      const lastDate = new Date(year, month + 1, 0).getDate();
      const days = [];

      for (let i = 0; i < startWeekday; i++) {
        days.push({ date: null, day: '' });
      }

      for (let d = 1; d <= lastDate; d++) {
        const dateStr = this.toDateStr(year, month, d);
        days.push({
          date: dateStr,
          day: d,
          disabled: this.isDateDisabled(dateStr),
          isStart: dateStr === this.form.checkIn,
          isEnd: dateStr === this.form.checkOut,
          inRange: !!(this.form.checkIn && this.form.checkOut && dateStr > this.form.checkIn && dateStr < this.form.checkOut),
          isToday: dateStr === this.today
        });
      }

      while (days.length % 7 !== 0) {
        days.push({ date: null, day: '' });
      }

      return { label: `${year}년 ${month + 1}월`, days };
    },

    toggleCalendar() {
      this.calendarOpen = !this.calendarOpen;
      if (this.calendarOpen) this.guestPanelOpen = false;
    },

    navCalendar(dir) {
      const b = this.calendarBase;
      this.calendarBase = new Date(b.getFullYear(), b.getMonth() + dir, 1);
    },

    selectDate(dateStr, disabled) {
      if (disabled) return;

      if (!this.form.checkIn || (this.form.checkIn && this.form.checkOut)) {
        // 새로운 체크인 선택 — 캘린더를 체크인 달로 재정렬
        this.form.checkIn  = dateStr;
        this.form.checkOut = '';

        const [y, m] = dateStr.split('-').map(Number);
        this.calendarBase = new Date(y, m - 1, 1);   // ✅ 추가 — 월말 체크인이어도 다음달이 오른쪽에 항상 보이게 됨

      } else {
        // 체크인은 있고 체크아웃 대기 중
        if (dateStr <= this.form.checkIn) {
          this.form.checkIn  = dateStr;
          this.form.checkOut = '';

          const [y, m] = dateStr.split('-').map(Number);
          this.calendarBase = new Date(y, m - 1, 1);   // ✅ 추가 — 재선택 시에도 동일하게 재정렬

        } else {
          this.form.checkOut = dateStr;
          this.calendarOpen  = false;
        }
      }
    },

    clearDates() {
      this.form.checkIn = '';
      this.form.checkOut = '';
    },

    formatDisplay(dateStr) {
      if (!dateStr) return '';
      const [y, m, d] = dateStr.split('-');
      return `${y}.${m}.${d}`;
    },

    addChild()     { if (this.form.children.length < 9) this.form.children.push(5); },
    removeChild(i) { if (i >= 0) this.form.children.splice(i, 1); },

    closePanels(e) {
      if (!e.target.closest('.sb-guests'))  this.guestPanelOpen = false;
      if (!e.target.closest('.sb-dates'))   this.calendarOpen = false;
      if (!e.target.closest('.sb-keyword')) this.keywordOpen = false;
    },

    registerCard(el, hotelId) {
      if (!el || !this.$_observer) return;
      el.dataset.hotelId = hotelId;
      const hotel = this.allHotels.find(h => h.hotel_id === hotelId);
      if (hotel?.heroImg && this.imageMap[hotelId] === undefined) {
        const heroUrl = `${this.rakutenImageHost}/${hotelId}/${hotel.heroImg}`;
        this.imageMap[hotelId]         = [heroUrl];
        this.carouselIndexMap[hotelId] = 0;
        this.imageLoadingMap[hotelId]  = false;
      }
      this.$_observer.observe(el);
    },

    async loadHotelImages(hotelId) {
      const current = this.imageMap[hotelId];
      if (this.imageLoadingMap[hotelId]) return;
      if (current?.length > 1) return;

      this.imageLoadingMap[hotelId] = true;
      try {
        const result = await this.$api('/api/rakutenHotelImages', { param: [hotelId] });
        const urls = (result || []).map(img =>
          `${this.rakutenImageHost}/${hotelId}/${img.image_url}`
        );
        if (urls.length) {
          this.imageMap[hotelId]         = urls;
          this.carouselIndexMap[hotelId] = 0;
        } else if (!current?.length) {
          this.imageMap[hotelId] = [];
        }
      } catch(e) {
        if (!current?.length) this.imageMap[hotelId] = [];
      } finally {
        this.imageLoadingMap[hotelId] = false;
      }
    },

    currentImage(hotelId) {
      const imgs = this.imageMap[hotelId] || [];
      return imgs[this.carouselIndexMap[hotelId] ?? 0] || '';
    },

    prevImage(hotelId) {
      const len = this.imageMap[hotelId]?.length || 0;
      if (!len) return;
      const cur = this.carouselIndexMap[hotelId] ?? 0;
      this.carouselIndexMap[hotelId] = (cur - 1 + len) % len;
    },

    nextImage(hotelId) {
      const len = this.imageMap[hotelId]?.length || 0;
      if (!len) return;
      const cur = this.carouselIndexMap[hotelId] ?? 0;
      this.carouselIndexMap[hotelId] = (cur + 1) % len;
    },

    setImage(hotelId, idx) { this.carouselIndexMap[hotelId] = idx; },

    validate() {
      this.validErr = { region: false, checkIn: false, checkOut: false, guests: false };
      let msg = '';
      if (!this.filter.city) 
      {
        this.validErr.region = true; msg = '지역을 선택해주세요.';
      } 
      else if (!this.form.checkIn) 
      {
        this.validErr.checkIn = true; msg = '체크인 날짜를 선택해주세요.';
      } 
      else if (!this.form.checkOut) 
      {
        this.validErr.checkOut = true; msg = '체크아웃 날짜를 선택해주세요.';
      } 
      else if (this.form.checkOut <= this.form.checkIn) 
      {
        this.validErr.checkOut = true; msg = '체크아웃은 체크인 이후여야 합니다.';
      } 
      else if (this.nightCount > 14) 
      {
        msg = `권장 숙박일(14박)을 초과합니다. 현재 ${this.nightCount}박`;
      } 
      else if (this.targetHotelIds.length === 0) 
      {
        msg = '검색할 호텔이 없습니다.';
      } 
      else if (this.form.adultCount < this.form.roomCount) 
      {
        this.validErr.guests = true;
        msg = `성인 수(${this.form.adultCount}명)가 객실 수(${this.form.roomCount}실)보다 적습니다.`;
      }
      return msg;
    },

    // 검색리드타임 단축을 위한 리팩토링(2026-07-06)
    async doSearch() {
      this.errMsg = this.validate();
      if (this.errMsg) 
      {
        this.loading = false;
        if (this.targetHotelIds.length === 0) {
          this.$swal({
            icon: 'warning',
            text: '조건에 부합하는 숙소가 없습니다.',
            timer: 2500, showConfirmButton: false
          });
          window.history.replaceState(null, '', window.location.pathname);
        }
        return;
      }

      this.loading   = true;
      this.hotels    = [];
      this.searched  = true;
      this.batchSessions = {};
      this.clearPollTimers();

      const batches = this.chunkArray(this.targetHotelIds, 50);
      this.batchesTotal = batches.length;   // ✅ 추가
      this.batchesDone  = 0;                // ✅ 추가

      let firstResponded = false;   // ✅ 추가 — 첫 배치 응답 여부

      const runBatch = async (batchIds, batchIdx) => {
        try 
        {
          const result = await this.$api('/api/rtx/searchHotels', {
            param: [{
              check_in_date:  this.form.checkIn,
              check_out_date: this.form.checkOut,
              hotel_ids:      batchIds,
              room_count:     this.form.roomCount,
              adult_count:    this.form.adultCount,
              children:       this.form.children
            }]
          });

          if (!result?.success) throw new Error(result?.message || '검색 실패');

          this.mergeHotels(result.hotels || []);
          this.batchSessions[batchIdx] = {
            session_id:     result.session_id,
            session_id_krw: result.session_id_krw
          };

          // ✅ 첫 응답이 오면 그때 스피너를 내림 (배치 발송 직후가 아니라)
          if (!firstResponded) {
            firstResponded = true;
            this.loading = false;
          }

          if (result.status !== 'complete') {
            this.pollBatchUntilComplete(batchIds, batchIdx);
          } else {
            this.batchesDone++;   // ✅ 완료 카운트
          }

        } 
        catch (e) 
        {
          console.error(`[RTX] 배치${batchIdx} 검색 실패:`, e);
          this.batchesDone++;     // ✅ 실패도 완료 처리(더 이상 기다리지 않음)
          if (!firstResponded) 
          {
            firstResponded = true;
            this.loading = false;
          }
        }
      };

      batches.forEach((batchIds, idx) => runBatch(batchIds, idx));

      // ⚠️ 기존의 this.loading = false; 즉시 호출은 제거 — 첫 응답 시점으로 이동
    },

    // ✅ 완료 안 된 배치를 세션 유지하며 짧은 간격으로 추가 폴링
    pollBatchUntilComplete(batchIds, batchIdx, tries = 0) {
      const MAX_TRIES = 6;
      if (tries >= MAX_TRIES) 
      {
        this.batchesDone++;   // ✅ 포기할 때도 완료 처리
        return;
      }

      const timer = setTimeout(async () => {
        const sess = this.batchSessions[batchIdx];
        if (!sess) { this.batchesDone++; return; }   // ✅ 세션 유실 시에도 완료 처리

        try 
        {
          const result = await this.$api('/api/rtx/searchWithSession', {
            param: [{
              session_id:     sess.session_id,
              session_id_krw: sess.session_id_krw,
              hotel_ids:      batchIds,
              check_in_date:  this.form.checkIn,
              check_out_date: this.form.checkOut,
              room_count:     this.form.roomCount,
              adult_count:    this.form.adultCount,
              children:       this.form.children
            }]
          });

          if (result?.success) 
          {
            this.mergeHotels(result.hotels || []);
            this.batchSessions[batchIdx] = {
              session_id:     result.session_id,
              session_id_krw: result.session_id_krw
            };

            if (result.status !== 'complete') 
            {
              this.pollBatchUntilComplete(batchIds, batchIdx, tries + 1);
            } 
            else 
            {
              this.batchesDone++;   // ✅ 완료 카운트
            }
          } 
          else 
          {
            this.batchesDone++;
          }
        } 
        catch (e) 
        {
          console.error(`[RTX] 배치${batchIdx} 추가폴링 실패:`, e);
          this.batchesDone++;   // ✅ 실패 시에도 완료 처리
        }
      }, 2500);

      this.pollTimers.push(timer);
    },

    // ✅ 새 결과를 기존 hotels 배열에 upsert 병합 (Vue3 반응성 트리거)
    mergeHotels(newHotels) {
      if (!newHotels.length) return;
      
      const map = new Map(this.hotels.map(h => [h.hotel_id, h]));
      newHotels.forEach(h => map.set(h.hotel_id, h));
      this.hotels = Array.from(map.values());

      // 가격 범위 슬라이더도 갱신
      this.$nextTick(() => {
        this.sideFilter.priceMin = this.priceRangeBounds.min;
        this.sideFilter.priceMax = this.priceRangeBounds.max;
      });
    },

    chunkArray(arr, size) {
      const result = [];
      for (let i = 0; i < arr.length; i += size) result.push(arr.slice(i, i + size));
      return result;
    },

    clearPollTimers() {
      this.pollTimers.forEach(t => clearTimeout(t));
      this.pollTimers = [];
    },

    checkAllComplete(totalBatches, getCompleted) {
      if (getCompleted() >= totalBatches && !this.hotels.length) {
        this.$swal({
          icon: 'info', title: '검색 결과 없음',
          text: '조건에 맞는 객실이 없습니다.',
          timer: 2500, showConfirmButton: false
        });
        window.history.replaceState(null, '', window.location.pathname);
      }
    },

    calcSellingKrw(hotelId, costKrw, minSellingKrw = null) {
      if (costKrw == null) return null;
      const m = this.marginMap[hotelId];
      if (!m) return costKrw;

      let selling;
      if      (m.marginMethod === 'P') selling = Math.round(costKrw * (1 + m.margin / 100));
      else if (m.marginMethod === 'A') selling = Math.round(costKrw + m.margin);
      else                             selling = costKrw;

      // ✅ minimum_selling_krw 하한선 보정
      if (minSellingKrw && selling < minSellingKrw) selling = minSellingKrw;

      return selling;
    },

    // ✅ 회원/비회원 분기
    doSelect(hotel) {
      const isLoggedIn = this.user.email;

      if (!isLoggedIn) {
        this.$swal({
          icon:  'question',
          title: '회원으로 예약하시겠습니까?',
          showDenyButton:    true,
          showCancelButton:  false,
          confirmButtonText: '로그인 하기',
          denyButtonText:    '비회원으로 진행',
        }).then(result => {
          if (result.isConfirmed) 
          {
            this.$router.push({
              path:  '/b2cLogin',
              query: { redirect: encodeURIComponent(this.$route.fullPath) }  // ✅ 인코딩
            });
          } 
          else if (result.isDenied) 
          {
            this._proceedToRoomPlan(hotel, false);
          }
        });
        return;
      }

      this._proceedToRoomPlan(hotel, true);
    },

    _proceedToRoomPlan(hotel, isMember) {
      const pm = this.priceMap[hotel.hotel_id];

      this.selectedHotel      = hotel;
      this.selectedSearchData = {
        check_in_date:  this.form.checkIn,
        check_out_date: this.form.checkOut,
        room_count:     this.form.roomCount,
        adult_count:    this.form.adultCount,
        children:       this.form.children,
        night_count:    this.nightCount,
        selling_krw:    pm?.selling_krw,
        cost_krw:       hotel.cost_krw,
        cancel_policy:  pm?.cancel_policy || null,
        session_id:     this.sessionId,
        rtxPackage:     hotel.rtxPackage,
        rtxSearch:      hotel.rtxSearch,
        
        // ✅ 추가 — 요약 패널 표시용
        room_desc:      pm?.room_desc     || '',
        supplier_desc:  pm?.supplier_desc || '',

        // ✅ 회원 정보
        isMember:       isMember,
        memFlag:        isMember ? 'Y'                       : 'N',
        memId:          isMember ? this.user.email           : null,              // 멤버아이디는 이메일
        memLevel:       isMember ? (() => { const lv = this.user.memLevel ?? 0; return lv === 'N' ? '1' : lv; })() : null,  //향후 다양한 멤버레벨 대응 위해 문자열로 저장, 'N'은 비회원과 구분하기 위해 레벨 1로 간주
        memShip:        isMember ? (this.user.isActiveMember ? 'Y' : null) : null,  // 회원+유료멤:Y,  회원+유료멤X: null, 비회원: null
        hotel_tel:      hotel.tel || null,                                          //호텔연락처
      };

      this.roomPlanMode = true;
    },

    onPlanSelected(planData) {
      this.selectedSearchData = { ...this.selectedSearchData, ...planData };
      this.roomPlanMode = false;
      this.bookingMode  = true;
    },

    onBookingComplete(bookingId) {
      this.$swal({
        icon: 'success', title: '예약 완료!',
        text: `예약 ID: ${bookingId}`, confirmButtonText: '확인'
      });
      this.bookingMode = false;
    },

    krw(val) {
      if (val == null) return '-';
      return new Intl.NumberFormat('ko-KR').format(val);
    },

    cancelSummary(policy) {
      if (!policy?.cancellation_policies?.length) return '정책 없음';
      const paid = policy.cancellation_policies.find(p => p.penalty_percentage === 100);
      if (paid) {
        const d = new Date(paid.date_from);
        return `${d.getMonth() + 1}/${d.getDate()} 이전 무료취소`;
      }
      return policy.remarks || '취소정책 확인 필요';
    }
  }
};
</script>


<style scoped>
/* ═══════════════════════════════════════════════
   전체 레이아웃
═══════════════════════════════════════════════ */
.rtx-wrap {
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 16px;
  font-family: 'Noto Sans KR', sans-serif;
}

/* ═══════════════════════════════════════════════
   검색바
═══════════════════════════════════════════════ */
.search-bar-wrap { margin-bottom: 28px; }

.search-bar {
  display: flex;
  align-items: stretch;
  background: #fff;
  border: 2px solid #1a1a2e;
  border-radius: 14px;
  overflow: visible;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}

.sb-field {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  flex: 1;
  min-width: 0;
  transition: background 0.15s;
}
.sb-field:hover    { background: #f8f9ff; }
.sb-field.sb-error { background: #fff5f5; }
.sb-region { flex: 1.3; }
.sb-type   { flex: 1.1; }
.sb-dates  { flex: 1.6; }
.sb-guests { flex: 1.2; }

.sb-icon { font-size: 18px; flex-shrink: 0; }

.sb-inner {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  width: 100%;
}

.sb-label {
  font-size: 10px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}
.sb-label em { color: #e53e3e; font-style: normal; }

.sb-select, .sb-input {
  border: none; outline: none;
  background: transparent;
  font-size: 14px; font-weight: 600;
  color: #1a1a2e; width: 100%; padding: 0;
  cursor: pointer; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}

.sb-guest-summary { font-size: 14px; font-weight: 600; color: #1a1a2e; white-space: nowrap; }

.keyword-clear {
  background: none; border: none; color: #bbb;
  font-size: 14px; cursor: pointer; padding: 0 4px; flex-shrink: 0;
}
.keyword-clear:hover { color: #e53e3e; }

.sb-suggest-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 6px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  z-index: 200;
  max-height: 320px;
  overflow-y: auto;
}
.suggest-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  transition: background 0.12s;
}
.suggest-item:hover { background: #ebf4ff; }
.suggest-name {
  font-weight: 600;
  color: #1a1a2e;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.suggest-loc {
  font-size: 11px;
  color: #999;
  white-space: nowrap;
  flex-shrink: 0;
}

.sb-date-summary {
  display: flex; align-items: center; gap: 6px;
  font-size: 14px; font-weight: 600; color: #1a1a2e; white-space: nowrap;
}
.sb-placeholder { color: #bbb; font-weight: 400; }
.sb-arrow { color: #bbb; font-size: 12px; }

/* 날짜 범위 캘린더 */
.sb-cal-panel {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  z-index: 200;
  min-width: 560px;
}

.cal-header {
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 12px;
}
.cal-nav {
  width: 28px; height: 28px;
  border: 1px solid #ddd; border-radius: 50%;
  background: #fff; cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s; flex-shrink: 0;
}
.cal-nav:hover { border-color: #1a1a2e; background: #f0f0f0; }

.cal-months-label {
  display: flex; gap: 80px;
  font-size: 14px; font-weight: 700; color: #1a1a2e;
}

.cal-months { display: flex; gap: 24px; }
.cal-month  { flex: 1; min-width: 0; }

.cal-weekdays {
  display: grid; grid-template-columns: repeat(7, 1fr);
  font-size: 11px; color: #999; text-align: center;
  margin-bottom: 6px; font-weight: 700;
}

.cal-days { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.cal-day {
  display: flex; align-items: center; justify-content: center;
  height: 32px; font-size: 13px; border-radius: 8px;
  cursor: pointer; color: #1a1a2e;
}
.cal-day:hover:not(.cal-disabled):not(.cal-empty) { background: #ebf4ff; }
.cal-empty    { cursor: default; }
.cal-disabled { color: #ddd; cursor: not-allowed; }
.cal-today    { font-weight: 800; text-decoration: underline; text-underline-offset: 3px; }
.cal-in-range { background: #ebf4ff; border-radius: 0; }
.cal-start, .cal-end {
  background: #1a1a2e; color: #fff; font-weight: 700;
}
.cal-start { border-radius: 8px 0 0 8px; }
.cal-end   { border-radius: 0 8px 8px 0; }
.cal-start.cal-end { border-radius: 8px; }

.cal-footer {
  display: flex; justify-content: space-between; align-items: center;
  margin-top: 12px; padding-top: 10px; border-top: 1px solid #f0f0f0;
}
.cal-hint  { font-size: 12px; color: #888; }
.cal-clear {
  background: none; border: none; color: #e53e3e;
  font-size: 12px; font-weight: 700; cursor: pointer;
}

.sb-nights {
  background: #ebf4ff; color: #2b6cb0;
  font-size: 13px; font-weight: 700;
  padding: 4px 10px; border-radius: 20px;
  white-space: nowrap; align-self: center; flex-shrink: 0;
}

.sb-divider { width: 1px; background: #e2e8f0; margin: 10px 0; flex-shrink: 0; }

.sb-search-btn {
  display: flex; align-items: center; gap: 6px;
  padding: 0 28px;
  background: #1a1a2e; color: #fff;
  border: none; border-radius: 0 12px 12px 0;
  font-size: 15px; font-weight: 700;
  cursor: pointer; white-space: nowrap;
  transition: background 0.2s; flex-shrink: 0;
}
.sb-search-btn:hover:not(:disabled) { background: #2e75b6; }
.sb-search-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.sb-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff; border-radius: 50%;
  animation: spin 0.7s linear infinite; display: inline-block;
}

.search-errmsg { margin-top: 8px; font-size: 12px; color: #e53e3e; padding-left: 4px; }
.search-info {
  margin-top: 8px; font-size: 12px; color: #666;
  display: flex; align-items: center; gap: 6px; padding-left: 4px;
}
.info-dot { width: 6px; height: 6px; background: #86cd53; border-radius: 50%; display: inline-block; }

/* ═══════════════════════════════════════════════
   인원 패널
═══════════════════════════════════════════════ */
.guest-panel {
  position: absolute; top: calc(100% + 10px); left: 0;
  background: #fff; border: 1px solid #e2e8f0;
  border-radius: 12px; padding: 16px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  z-index: 100; min-width: 240px;
}
.guest-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 0; border-bottom: 1px solid #f0f0f0;
  font-size: 14px; font-weight: 600;
}
.guest-row:last-of-type { border-bottom: none; }
.guest-ctrl { display: flex; align-items: center; gap: 12px; }
.gc-btn {
  width: 28px; height: 28px;
  border: 1px solid #ddd; border-radius: 50%;
  background: #fff; cursor: pointer; font-size: 16px;
  display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.gc-btn:hover:not(:disabled) { border-color: #1a1a2e; background: #f0f0f0; }
.gc-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.gc-val { min-width: 20px; text-align: center; font-size: 15px; font-weight: 700; }

.child-ages { padding: 8px 0; }
.child-age-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 6px 0; font-size: 13px; color: #555;
}
.guest-done-btn {
  width: 100%; margin-top: 12px; padding: 8px;
  background: #1a1a2e; color: #fff;
  border: none; border-radius: 8px;
  font-size: 13px; font-weight: 700; cursor: pointer;
}

/* ═══════════════════════════════════════════════
   로딩
═══════════════════════════════════════════════ */
.loading-wrap { display: flex; justify-content: center; padding: 60px 0; }
.loading-card {
  text-align: center; padding: 40px 60px;
  background: #fff; border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.08);
}
.loading-anim {
  display: flex; justify-content: center; align-items: flex-end;
  gap: 5px; height: 40px; margin-bottom: 16px;
}
.ld-bar {
  width: 6px; background: #2e75b6; border-radius: 3px;
  animation: ldwave 0.8s ease-in-out infinite alternate;
}
@keyframes ldwave {
  from { height: 8px; opacity: 0.4; }
  to   { height: 36px; opacity: 1; }
}
.loading-card p { color: #555; font-size: 14px; margin: 0; }

/* ═══════════════════════════════════════════════
   안내 / 결과 헤더
═══════════════════════════════════════════════ */
.empty-guide    { text-align: center; padding: 80px 0; color: #999; }
.guide-icon     { font-size: 64px; margin-bottom: 16px; }
.empty-guide h3 { color: #555; margin-bottom: 8px; }

.results-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.results-title  { font-size: 15px; color: #333; }
.results-title strong { font-size: 18px; color: #1a1a2e; }
.results-date   { font-size: 13px; color: #888; margin-left: 8px; }

.sort-select {
  padding: 6px 12px; border: 1px solid #ddd;
  border-radius: 8px; font-size: 13px; color: #444; cursor: pointer;
}

/* ═══════════════════════════════════════════════
   호텔 카드 — 아고다 스타일
═══════════════════════════════════════════════ */
.card-list { display: flex; flex-direction: column; gap: 12px; }

.hotel-card {
  display: flex;
  background: #fff;
  border: 1px solid #e8edf2;
  border-radius: 14px;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
  min-height: 185px;
}
.hotel-card:hover  { box-shadow: 0 8px 28px rgba(0,0,0,0.10); transform: translateY(-2px); }
.card-has-price    { border-color: #c6e6a0; }

/* 이미지 */
.card-img-wrap {
  position: relative; width: 230px;
  flex-shrink: 0; overflow: hidden;
}
.card-img { width: 230px; height: 230px; object-fit: cover; display: block; }
.card-img-placeholder {
  width: 100%; height: 100%; min-height: 160px;
  display: flex; align-items: center; justify-content: center;
  background: #f0f4f8; font-size: 40px;
}
.img-loading { background: #f0f4f8; }
.img-spinner {
  width: 28px; height: 28px;
  border: 3px solid rgba(46,117,182,0.2);
  border-top-color: #2e75b6; border-radius: 50%;
  animation: spin 0.7s linear infinite;
}

/* 캐러셀 */
.carousel-btn {
  position: absolute; top: 50%; transform: translateY(-50%);
  background: rgba(0,0,0,0.45); color: #fff;
  border: none; border-radius: 50%;
  width: 28px; height: 28px; font-size: 18px;
  cursor: pointer; z-index: 5;
  display: flex; align-items: center; justify-content: center;
  opacity: 0; transition: opacity 0.2s;
}
.card-img-wrap:hover .carousel-btn { opacity: 1; }
.carousel-prev { left: 6px; }
.carousel-next { right: 6px; }
.carousel-btn:hover { background: rgba(0,0,0,0.7); }
.carousel-dots {
  position: absolute; bottom: 8px; left: 50%;
  transform: translateX(-50%); display: flex; gap: 4px; z-index: 5;
}
.carousel-dot {
  width: 6px; height: 6px; border-radius: 50%;
  background: rgba(255,255,255,0.5); cursor: pointer;
  transition: background 0.15s, transform 0.15s;
}
.dot-active { background: #fff; transform: scale(1.3); }
.carousel-counter {
  position: absolute; top: 8px; right: 8px;
  background: rgba(0,0,0,0.45); color: #fff;
  font-size: 10px; font-weight: 700;
  padding: 2px 7px; border-radius: 10px;
  backdrop-filter: blur(2px); z-index: 5;
}
.card-type-badge {
  position: absolute; top: 10px; left: 10px;
  background: rgba(0,0,0,0.55); color: #fff;
  font-size: 10px; font-weight: 700;
  padding: 3px 8px; border-radius: 8px; backdrop-filter: blur(4px);
}

/* 호텔 정보 */
.card-info {
  flex: 1; padding: 16px 18px;
  display: flex; flex-direction: column; gap: 6px; min-width: 0;
}
.card-hotel-name {
  font-size: 16px; font-weight: 800; color: #1a1a2e;
  line-height: 1.3; white-space: nowrap;
  overflow: hidden; text-overflow: ellipsis;
}
.hotel-grade-badge {
  display: inline-block; font-size: 11px; font-weight: 700;
  padding: 2px 7px; border-radius: 8px; margin-left: 6px;
  vertical-align: middle; flex-shrink: 0;
}
.grade-has  { background: #fff8e1; color: #b7791f; border: 1px solid #ded221; }
.grade-none { background: #f5f5f5; color: #aaa;    border: 1px solid #e0e0e0; }
.card-location { font-size: 12px; color: #666; display: flex; align-items: center; gap: 4px; }
.loc-icon      { font-size: 12px; }
.card-hotel-id { color: #bbb; font-size: 11px; }

/* 객실 태그 */
.card-room-tags { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 2px; }
.room-tag {
  font-size: 11px; font-weight: 600;
  padding: 3px 8px; border-radius: 6px; white-space: nowrap;
}
.rt-room   { background: #f0f4ff; color: #3b5bdb; }
.rt-bed    { background: #f5f5f5; color: #555; }
.rt-food   { background: #fff8e6; color: #c47f00; }
.rt-cancel { background: #f0fff4; color: #276749; }

/* 평점 */
.card-rating-row { display: flex; gap: 8px; flex-wrap: wrap; }
.rating-badge {
  display: flex; align-items: center; gap: 6px;
  background: #1a6eb5; border-radius: 6px 6px 6px 0; padding: 5px 10px;
}
.rating-badge-rtx { background: #2d7d46; }
.rating-score  { font-size: 18px; font-weight: 900; color: #fff; line-height: 1; }
.rating-meta   { display: flex; flex-direction: column; gap: 1px; }
.rating-label-text { font-size: 11px; font-weight: 700; color: #fff; line-height: 1; }
.rating-source { font-size: 10px; color: rgba(255,255,255,0.75); line-height: 1; }

/* 가격 영역 */
.card-price-wrap {
  width: 210px; flex-shrink: 0;
  padding: 16px 18px;
  background: #fafbfc; border-left: 1px solid #eee;
  display: flex; flex-direction: column;
  justify-content: space-between; gap: 8px;
}

.price-pending {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  flex: 1; color: #bbb; font-size: 12px;
  text-align: center; gap: 8px; line-height: 1.6;
}
.price-pending-icon { font-size: 24px; }

.price-block { display: flex; flex-direction: column; gap: 4px; }

.price-refund-top { text-align: right; }
.badge-r  { font-size: 10px; font-weight: 700; background: #d4edda; color: #155724; padding: 2px 8px; border-radius: 10px; }
.badge-nr { font-size: 10px; font-weight: 700; background: #f8d7da; color: #721c24; padding: 2px 8px; border-radius: 10px; }

.price-selling-main {
  font-size: 24px; font-weight: 900; color: #1a1a2e;
  text-align: right; letter-spacing: -0.5px;
  line-height: 1.1; margin: 2px 0;
}
.price-selling-main small { font-size: 13px; font-weight: 500; }

.price-per-night { font-size: 11px; color: #999; text-align: right; margin-bottom: 2px; }

.price-extra-fees { display: flex; justify-content: space-between; align-items: center; font-size: 10px; color: #aaa; margin-top: 2px; }
.extra-val { font-weight: 600; }

.btn-book {
  padding: 10px; background: #1a6eb5; color: #fff;
  border: none; border-radius: 8px;
  font-size: 14px; font-weight: 700; cursor: pointer;
  text-align: center; transition: background 0.2s; width: 100%;
}
.btn-book:hover { background: #86cd53; }

/* ═══════════════════════════════════════════════
   결과 없음
═══════════════════════════════════════════════ */
.empty-result    { text-align: center; padding: 60px; background: #fff; border-radius: 14px; }
.empty-icon      { font-size: 56px; margin-bottom: 16px; }
.empty-result h3 { color: #333; margin-bottom: 8px; }
.empty-result p  { color: #888; }

/* ═══════════════════════════════════════════════
   공통 / 반응형
═══════════════════════════════════════════════ */
@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 768px) {
  .rtx-wrap        { padding-top: 70px; }
  .search-bar      { flex-direction: column; border-radius: 12px; }
  .sb-divider      { width: 100%; height: 1px; margin: 0; }
  .sb-search-btn   { border-radius: 0 0 12px 12px; padding: 14px; justify-content: center; }
  .sb-cal-panel    { min-width: 280px; left: 0; right: 0; }
  .cal-months      { flex-direction: column; gap: 16px; }
  .cal-months-label{ gap: 20px; }

  .hotel-card      { flex-direction: column; overflow: hidden; width: 100%; box-sizing: border-box; }
  .card-img-wrap   { width: 100% !important; height: 220px; }
  .card-img        { width: 100% !important; height: 220px; object-fit: cover; }

  .card-price-wrap {
    width: 100%;
    border-left: none;
    border-top: 1px solid #eee;
    box-sizing: border-box;
  }

  .price-selling-main {
    font-size: 20px;
    word-break: keep-all;
  }

  .card-hotel-name {
    white-space: normal;
    word-break: break-word;
  }

  .card-info {
    width: 100%;
    box-sizing: border-box;
  }

  .card-room-tags { flex-wrap: wrap; }

  .card-area     { overflow: hidden; width: 100%; }
  .card-list     { width: 100%; overflow: hidden; }

  .results-wrap  { overflow-x: hidden; }
  .results-body  { overflow-x: hidden; }

  .btn-book { width: 100%; }
}

/**
  좌측맞춤검색
*/
.fp-price-labels {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 11px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 12px;
  gap: 4px;
}
.fp-price-labels span:nth-child(2) { color: #aaa; }

.fp-dual-slider {
  position: relative;
  height: 20px;
}

.fp-track {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  pointer-events: none;
}

.fp-track-fill {
  position: absolute;
  top: 0; bottom: 0;
  background: #86cd53;
  border-radius: 2px;
}

.fp-range {
  position: absolute;
  width: 100%;
  height: 4px;
  background: transparent;
  -webkit-appearance: none;
  outline: none;
  pointer-events: none;
  top: 50%;
  transform: translateY(-50%);
  margin: 0;
}

.fp-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #1a1a2e;
  cursor: pointer;
  pointer-events: all;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.25);
  transition: transform 0.1s;
}
.fp-range::-webkit-slider-thumb:hover {
  transform: scale(1.2);
  background: #86cd53;
}

.fp-range-min { z-index: 3; }
.fp-range-max { z-index: 4; }

.results-body {
  display: flex;
  gap: 20px;
  align-items: flex-start;
}
.card-area { flex: 1; min-width: 0; }

.filter-result-count {
  font-size: 14px;
  color: #333;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.filter-result-count strong { font-size: 18px; color: #1a1a2e; }
.filter-active-badge {
  font-size: 11px;
  background: #ebf4ff;
  color: #2b6cb0;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
}

.filter-panel {
  width: 220px;
  flex-shrink: 0;
  background: #fff;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 16px;
  position: sticky;
  top: 80px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}
.filter-panel::-webkit-scrollbar { width: 4px; }
.filter-panel::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

.fp-title {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  font-weight: 800;
  color: #1a1a2e;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 2px solid #1a1a2e;
}
.fp-reset {
  font-size: 11px;
  color: #888;
  background: none;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 2px 8px;
  cursor: pointer;
  transition: all 0.15s;
}
.fp-reset:hover { border-color: #e53e3e; color: #e53e3e; }

.fp-section {
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 1px solid #f0f0f0;
}
.fp-section:last-child { border-bottom: none; margin-bottom: 0; }

.fp-section-title {
  font-size: 11px;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
}

.fp-price-labels {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  font-weight: 700;
  color: #1a1a2e;
  margin-bottom: 6px;
}
.fp-slider-wrap {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.fp-range {
  width: 100%;
  height: 4px;
  -webkit-appearance: none;
  background: #e2e8f0;
  border-radius: 2px;
  outline: none;
  cursor: pointer;
}
.fp-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px; height: 16px;
  border-radius: 50%;
  background: #1a1a2e;
  cursor: pointer;
  border: 2px solid #fff;
  box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}

.fp-check-list { display: flex; flex-direction: column; gap: 7px; }
.fp-check-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
  line-height: 1.4;
}
.fp-check-item input[type="checkbox"] {
  width: 14px; height: 14px;
  accent-color: #1a1a2e;
  cursor: pointer;
  flex-shrink: 0;
}
.fp-check-item input[type="radio"] {
  width: 14px; height: 14px;
  accent-color: #1a1a2e;
  cursor: pointer;
  flex-shrink: 0;
}
.fp-stars { color: #f0a500; font-size: 11px; letter-spacing: -1px; }
.fp-count { color: #bbb; font-size: 10px; margin-left: auto; }

.fp-more-btn {
  width: 100%;
  margin-top: 8px;
  padding: 5px 0;
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  color: #888;
  cursor: pointer;
  transition: all 0.15s;
}
.fp-more-btn:hover {
  border-color: #1a1a2e;
  color: #1a1a2e;
  background: #f8f9ff;
}

.fp-toggle-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #333;
  cursor: pointer;
}
.fp-toggle-item input[type="checkbox"] {
  width: 14px; height: 14px;
  accent-color: #86cd53;
  cursor: pointer;
}

@media (max-width: 768px) {
  .results-body  { flex-direction: column; }
  .filter-panel  { width: 100%; position: static; max-height: none; }
}

/* ═══════════════════════════════════════════════
   모바일 필터 바
═══════════════════════════════════════════════ */
.mobile-filter-bar   { display: none; }
.filter-drawer         { display: none; }
.filter-drawer-overlay { display: none; }

@media (max-width: 768px) {

  .filter-panel { display: none; }

  .mobile-filter-bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }

  .mobile-filter-btn {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    background: #fff;
    border: 1.5px solid #1a1a2e;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 700;
    color: #1a1a2e;
    cursor: pointer;
    transition: all 0.15s;
  }
  .mobile-filter-btn:hover { background: #1a1a2e; color: #fff; }

  .mobile-filter-badge {
    background: #e53e3e;
    color: #fff;
    font-size: 10px;
    font-weight: 800;
    padding: 1px 6px;
    border-radius: 10px;
    min-width: 18px;
    text-align: center;
  }

  .mobile-result-count        { font-size: 13px; color: #555; }
  .mobile-result-count strong { font-size: 16px; color: #1a1a2e; }

  .filter-drawer-overlay {
    display: block;
    position: fixed;
    inset: 0;
    background: rgba(0,0,0,0.5);
    z-index: 300;
  }

  .filter-drawer {
    display: flex;
    position: fixed;
    top: 60px;
    right: 0; 
    bottom: 0;
    width: 85vw;
    max-width: 320px;
    background: #fff;
    z-index: 301;
    flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: -4px 0 20px rgba(0,0,0,0.15);
  }
  .filter-drawer.drawer-open { transform: translateX(0); }

  .drawer-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px;
    border-bottom: 2px solid #1a1a2e;
    font-size: 14px;
    font-weight: 800;
    color: #1a1a2e;
    flex-shrink: 0;
  }
  .drawer-header-btns { display: flex; align-items: center; gap: 8px; }
  .drawer-close-btn {
    background: none; border: none;
    font-size: 18px; color: #888;
    cursor: pointer; padding: 0 4px;
  }

  .drawer-body {
    flex: 1; overflow-y: auto; padding: 16px;
  }
  .drawer-body::-webkit-scrollbar       { width: 4px; }
  .drawer-body::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 2px; }

  .drawer-footer { padding: 16px; border-top: 1px solid #e8edf2; flex-shrink: 0; }
  .drawer-apply-btn {
    width: 100%; padding: 14px;
    background: #1a1a2e; color: #fff;
    border: none; border-radius: 10px;
    font-size: 15px; font-weight: 700;
    cursor: pointer; transition: background 0.2s;
  }
  .drawer-apply-btn:hover { background: #86cd53; }
}

.price-pending-spinner {
  width: 20px; height: 20px;
  border: 2px solid rgba(46,117,182,0.2);
  border-top-color: #2e75b6; border-radius: 50%;
  animation: spin 0.7s linear infinite;
}
</style>